import {Component, ElementRef, OnInit} from '@angular/core';
import {StructuredContentService} from '../service/structured-content.service';
import {StructuredContent, TariffOption, TariffsConfiguration} from '../model/structured-content-models';
import {TariffStatusChangedEventArgs} from '../model/tariff-status-changed-event-args';
import {ContextType, PriceBoxCommunicationService} from '../service/price-box-communication.service';
import {PriceBoxUpdateArgument} from '../model/price-box-update-event-argument';
import {TariffConfigurationService} from '../service/tariff-configuration-service';
import {ProfilerCommunicationService} from '../service/profiler-communication.service';
import {ApiService} from '../service/api.service';
import {TrackingService} from '../service/tracking.service';
import {TariffDataApiService} from '../service/tariff-data-api.service';
import {TariffOptionConfiguration} from '../model/tariff-option-configuration';
import {FieldConfiguration} from '../model/field-configuration';
import {PricesWrapper} from '../model/prices-wrapper';


export class ErrorStatus {
  public msg: string;
  public hasOccured: boolean;

  constructor(msg: string, hasOccured: boolean) {
    this.msg = msg;
    this.hasOccured = hasOccured;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  static DESC_PRICE_TOTAL = 'Summe';
  static DESC_PRICE_EMPLOYER = 'Arbeitgeberanteil';
  static DESC_PRICE_CUSTOMER = 'Kundenanteil';
  static KEY_SHOW_CTA = 'CTAsAnzeigen';

  title = 'ukv-vollversicherung';

  private sessionId = '';
  private mandantId = '';

  public bausteinRowList: any;
  public structuredContentService: StructuredContentService;
  public priceBoxComSvc: PriceBoxCommunicationService;
  public tariffConfigSvc: TariffConfigurationService;
  public profilerComSvc: ProfilerCommunicationService;
  public apiSvc: ApiService;
  private trackingSvc: TrackingService;
  private tariffDataApiSvc: TariffDataApiService;

  constructor(structuredContentService: StructuredContentService,
              appElementRef: ElementRef,
              priceBoxComSvc: PriceBoxCommunicationService,
              tariffConfigSvc: TariffConfigurationService,
              profilerComSvc: ProfilerCommunicationService,
              apiSvc: ApiService,
              trackingSvc: TrackingService,
              tariffDataApiSvc: TariffDataApiService) {

    this.structuredContentService = structuredContentService;

    this.apiSvc = apiSvc;

    const config: StructuredContent = JSON.parse(appElementRef.nativeElement.getAttribute('configuration')) as StructuredContent;
    // tslint:disable-next-line:max-line-length
    const tariffsConfig: TariffsConfiguration = this.convertTariffsConfigurationNumbers(JSON.parse(appElementRef.nativeElement.getAttribute('tariffconfiguration')) as TariffsConfiguration);
    this.mandantId = appElementRef.nativeElement.getAttribute('mandantId') as string;
    this.sessionId = appElementRef.nativeElement.getAttribute('sessionId') as string;
    this.apiSvc.baseUrl = appElementRef.nativeElement.getAttribute('apiUrl') as string;
    this.apiSvc.product = config.product;

    this.tariffDataApiSvc = tariffDataApiSvc;
    this.tariffDataApiSvc.product = config.product;

    this.structuredContentService.setConfig(config);
    this.bausteinRowList = structuredContentService.getBausteinRowList();

    this.priceBoxComSvc = priceBoxComSvc;
    this.tariffConfigSvc = tariffConfigSvc;
    this.tariffConfigSvc.setTariffsConfig(tariffsConfig);

    this.profilerComSvc = profilerComSvc;
    this.trackingSvc = trackingSvc;
  }

  ngOnInit() {
    this.tariffConfigSvc.buildFromStructuredContent(this.structuredContentService.getConfig());

    // we initially want to get the default configuration
    this.setDefaultValueIndicator(true);

    this.initializePriceBox();

    this.profilerComSvc.listen(this.onProfilerUpdated, this);
  }

  initializePriceBox() {
    this.refresh();
  }

  refresh() {
    this.tariffConfigSvc.calculate(this.sessionId, this.mandantId).then((value: any) => {
      this.tariffConfigSvc.postProcess();
      this.propagateTariffData();
    }).catch((error: any) => {

    }).finally(() => {
      // reset the indicator to use default values
      this.setDefaultValueIndicator(false);
    });
  }

  onTariffStatusChanged() {
    this.refresh();
  }

  onTariffActiveStatusChanged(args: TariffStatusChangedEventArgs) {
    // update the entry identified by args.id in the tariff configuration
    const tariffOption = this.tariffConfigSvc.getTariffOptionById(args.id);
    if (!tariffOption) {
      console.error('Tariff option with calculationId: ' + args.id + ' does not exist');
      return;
    }
    tariffOption.active = args.isActive;
    this.refresh();
  }

  onProfilerUpdated() {
    // since the profiler values changed, set indicator for the backend to use default values
    this.setDefaultValueIndicator(true);
    this.refresh();
  }

  private setDefaultValueIndicator(val: boolean): void {
    if(!this.apiSvc.isUsingDefaultProfileValues()) { return; }
    const toc = this.tariffConfigSvc.getTariffOptionById(TariffConfigurationService.CALCULATION_ID_DEFAULT_VALUE_INDICATOR);
    toc.active = val;
  }

  propagateTariffData() {
    // update corresponding entry in price box
    const config: StructuredContent = this.structuredContentService.getConfig();

    // send updates for ukv, bk or both price boxes depending on the configured config.contextId
    if (config.contextId === ContextType.VMK) {
      this.priceBoxComSvc.triggerPriceUpdate(this.buildPriceBoxUpdateForContext(ContextType.UKV), ContextType.UKV);
      this.priceBoxComSvc.triggerPriceUpdate(this.buildPriceBoxUpdateForContext(ContextType.BK), ContextType.BK);

      this.trackingSvc.updatePriceBoxTrackingData(ContextType.UKV, this.tariffConfigSvc.tariffOptions);
      this.trackingSvc.updatePriceBoxTrackingData(ContextType.BK, this.tariffConfigSvc.tariffOptions);
    } else {
      this.priceBoxComSvc.triggerPriceUpdate(this.buildPriceBoxUpdateForContext(config.contextId), config.contextId);

      this.trackingSvc.updatePriceBoxTrackingData(config.contextId, this.tariffConfigSvc.tariffOptions);
    }
    this.tariffDataApiSvc.storeTariffData(this.tariffConfigSvc.tariffOptions, this.tariffConfigSvc.prices);
  }

  buildPriceBoxUpdateForContext(context: ContextType): Array<PriceBoxUpdateArgument> {
    const args: Array<PriceBoxUpdateArgument> = new Array<PriceBoxUpdateArgument>();

    let totalPrice = 0;

    this.tariffConfigSvc.tariffOptions.forEach((toc: TariffOptionConfiguration, calculationId: string) => {

      if (!toc.active) {
        return;
      }

      const tariffOption: TariffOption = this.structuredContentService.getTariffOptionByCalculationId(calculationId);
      if (tariffOption === null) {
        return;
      }
      const pricesWrapper = this.tariffConfigSvc.prices.get(calculationId);
      const price = context === ContextType.UKV ? pricesWrapper.priceUkv : pricesWrapper.priceBk;
      totalPrice += price;

      // tslint:disable-next-line:max-line-length
      args.push(new PriceBoxUpdateArgument(tariffOption.shortName, tariffOption.calculationId, this.getBannerText(toc.fields), this.formatPrice(price)
      ));
    });

    args.push(new PriceBoxUpdateArgument(AppComponent.DESC_PRICE_TOTAL, '', '', this.formatPrice(totalPrice)));
    args.push(new PriceBoxUpdateArgument(AppComponent.KEY_SHOW_CTA, '', '', totalPrice > 0 ? 'true' : 'false'));

    if(this.apiSvc.isUsingEmployersContribution()) {
      const employersContribution = this.getEmployersContribution(context !== ContextType.UKV);

      if (employersContribution > 0) {
        args.push(new PriceBoxUpdateArgument(AppComponent.DESC_PRICE_EMPLOYER, '', '',
          this.formatPrice(employersContribution)));
      }

      args.push(new PriceBoxUpdateArgument(AppComponent.DESC_PRICE_CUSTOMER, '', '',
        this.formatPrice(totalPrice - employersContribution)));
    }
    return args;
  }

  public getBannerText(tocFields: {}): string {
    let result = '';
    Object.keys(tocFields).forEach((key: string) => {
      const fc = tocFields[key];
      const desc = this.getFieldValueDescription(fc);
      if (desc) {
        result += ((result !== '') ? ', ' : '') + desc;
      }
    });
    return result;
  }

  getFieldValueDescription(fc: FieldConfiguration): string | null {
    for (const kvp of fc.options) {
      if (kvp.key === fc.value) {
        return kvp.value;
      }
    }
    return null;
  }

  formatPrice(price: number): string {
    return price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  convertTariffsConfigurationNumbers(tc: TariffsConfiguration): TariffsConfiguration {
    tc.tariffCategories.forEach((tariffCategory) => {
      tariffCategory.factor = Number.parseFloat(tariffCategory.factor.toString());
      tariffCategory.maxValue = Number.parseFloat(tariffCategory.maxValue.toString());
    });
    return tc;
  }

  private getEmployersContribution(useBk: boolean): number {
    /*if (!this.tariffConfigSvc.tariffOptions.get(
    TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION).active) {
      return 0;
    }*/
    const employersContributions: PricesWrapper = this.tariffConfigSvc.prices.get(
      TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION);
    return !useBk ? employersContributions.priceUkv : employersContributions.priceBk;
  }
}
